<template>
  <div class="submit-request-page bg-neutral-100 min-h-screen">
    <!-- 页面头部 -->
    <section class="py-12 bg-white border-b border-neutral-200">
      <div class="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h1 class="text-3xl md:text-4xl font-bold text-neutral-900 mb-4">
          提出工具需求
        </h1>
        <p class="text-neutral-500 text-base max-w-2xl mx-auto">
          告诉我们您需要什么类型的AI工具，我们的社区和开发者会为您推荐或开发合适的解决方案
        </p>
      </div>
    </section>

    <!-- 表单区域 -->
    <section class="py-8">
      <div class="container mx-auto px-4 sm:px-6 lg:px-8">
        <div
          class="max-w-3xl mx-auto bg-white rounded-xl p-6 md:p-8 border border-neutral-200 shadow-sm"
        >
          <h2 class="text-xl font-bold text-neutral-900 mb-6">提交需求</h2>

          <form @submit.prevent="handleSubmit" class="space-y-6">
            <!-- 需求标题 -->
            <div>
              <label
                for="request-title"
                class="block text-sm font-medium text-neutral-700 mb-2"
              >
                需求标题 <span class="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="request-title"
                v-model="form.title"
                required
                class="w-full px-4 py-2.5 rounded-lg border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-200"
                placeholder="简要描述您的需求，例如：需要一个古诗词学习工具"
              />
            </div>

            <!-- 需求分类 -->
            <div>
              <label
                for="request-category"
                class="block text-sm font-medium text-neutral-700 mb-2"
              >
                需求分类 <span class="text-red-500">*</span>
              </label>
              <div class="relative w-full">
                <select
                  id="request-category"
                  v-model="form.categoryId"
                  required
                  class="w-full px-4 py-2.5 rounded-lg border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-200 appearance-none bg-white"
                >
                  <option value="" disabled>请选择分类</option>
                  <option
                    v-for="item in categories"
                    :value="item.id"
                    :key="item.id"
                  >
                    {{ item.title }}
                  </option>
                </select>
                <!-- 自定义下拉箭头 -->
                <div
                  class="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none"
                >
                  <i class="fa fa-chevron-down text-neutral-400 text-sm"></i>
                </div>
              </div>
            </div>

            <!-- 详细描述 -->
            <div>
              <label
                for="request-description"
                class="block text-sm font-medium text-neutral-700 mb-2"
              >
                详细描述 <span class="text-red-500">*</span>
              </label>
              <textarea
                id="request-description"
                v-model="form.content"
                required
                rows="5"
                class="w-full px-4 py-3 rounded-lg border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-200 resize-y"
                placeholder="详细描述您的需求，包括：
1. 您希望工具解决什么问题？
2. 工具应该具备哪些功能？
3. 使用场景是什么？
4. 其他特殊要求..."
              ></textarea>
              <p class="mt-2 text-xs text-neutral-500">
                详细的需求描述有助于其他老师更好地理解您的需求并推荐合适的工具
              </p>
            </div>

            <!-- 联系方式 -->
            <div>
              <label
                for="request-contact"
                class="block text-sm font-medium text-neutral-700 mb-2"
              >
                联系方式 <span class="text-neutral-400">（选填）</span>
              </label>
              <input
                type="text"
                id="request-contact"
                v-model="form.phone"
                class="w-full px-4 py-2.5 rounded-lg border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-200"
                placeholder="邮箱或微信号，方便其他老师联系您"
              />
            </div>

            <!-- 提交按钮 -->
            <div class="pt-2">
              <button
                type="submit"
                :disabled="submitting"
                class="w-full px-6 py-3 rounded-lg bg-primary text-white font-medium hover:bg-primary/90 transition-all duration-200 shadow-sm disabled:bg-neutral-300 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                <i class="fa fa-paper-plane"></i>
                <span>{{ submitting ? "提交中..." : "提交需求" }}</span>
              </button>
            </div>
          </form>

          <!-- 提示信息 -->
          <div
            class="mt-6 p-4 bg-primary/5 rounded-lg border border-primary/10"
          >
            <div class="flex items-start space-x-3">
              <i class="fa fa-info-circle text-primary mt-0.5"></i>
              <div class="text-sm text-neutral-600">
                <p class="font-medium text-neutral-700 mb-1">提交需求后：</p>
                <ul class="space-y-1 list-disc list-inside">
                  <li>其他老师可以看到您的需求</li>
                  <li>老师可以推荐合适的工具给您</li>
                  <li>您会收到工具推荐通知</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 最新需求区域 -->
    <section class="py-8 pb-16">
      <div class="container mx-auto px-4 sm:px-6 lg:px-8">
        <div class="max-w-3xl mx-auto">
          <h2 class="text-xl font-bold text-neutral-900 mb-6">最新需求</h2>

          <!-- 需求列表 -->
          <div v-if="requests.length > 0" class="space-y-4">
            <div
              v-for="request in requests"
              :key="request.id"
              class="bg-white rounded-xl p-5 border border-neutral-200 shadow-sm hover:shadow-md transition-shadow duration-200 cursor-pointer"
              @click="openRequestDetail(request)"
            >
              <div class="flex items-start justify-between mb-3">
                <h3 class="font-semibold text-neutral-900">
                  {{ request.title }}
                </h3>
                <span
                  class="text-xs px-2 py-1 bg-neutral-100 text-neutral-600 rounded-full"
                >
                  {{ request.categoryName }}
                </span>
              </div>
              <p class="text-sm text-neutral-500 line-clamp-2 mb-3 whitespace-pre-line">
                {{ request.content }}
              </p>
              <div
                class="flex items-center justify-between text-xs text-neutral-400"
              >
                <span>{{ request.uname }} · {{ new Date(request.createTime).format('yyyy-MM-dd') }}</span>
                <span class="flex items-center space-x-1">
                  <i class="fa fa-comment-o"></i>
                  <span>{{ request.replyCount || 0 }} 条回复</span>
                </span>
              </div>
            </div>
          </div>

          <!-- 空状态 -->
          <div
            v-else
            class="bg-white rounded-xl p-12 border border-neutral-200 text-center"
          >
            <div
              class="w-16 h-16 mx-auto mb-4 rounded-full bg-neutral-100 flex items-center justify-center"
            >
              <i class="fa fa-lightbulb-o text-3xl text-neutral-400"></i>
            </div>
            <h3 class="text-lg font-medium text-neutral-700 mb-2">暂无需求</h3>
            <p class="text-sm text-neutral-500">成为第一个提出需求的用户吧！</p>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { globalState } from "@/state";
import { requireLogin } from "../utils/authGuard";

export default {
  name: "SubmitRequestPage",
  data() {
    return {
      submitting: false,
      categories: [],
      requests: [],
      editingId: "",
      form: {
        title: "",
        categoryId: "",
        content: "",
        phone: "",
      },
    };
  },
  created() {
    // 访客模式：提出需求需要登录（防止直接访问 URL 绕过入口拦截）
    if (!requireLogin(this, "提出需求")) return;
    this.editingId = this.$route.query.id || "";
    this.getCategories();
    this.getRequests();
    if (this.editingId) this.loadDemand();
  },
  methods: {
    // 编辑：回填需求内容
    async loadDemand() {
      try {
        const res = await this.$axios({ url: "demand/" + this.editingId });
        const d = res.code === 200 ? res.data : null;
        if (!d) {
          alert("需求不存在或已删除");
          return;
        }
        const me = (this.$store && this.$store.state.user) || {};
        if (d.uid && me.uid && String(d.uid) !== String(me.uid)) {
          alert("只能修改自己提出的需求");
          this.$router.replace("/request");
          return;
        }
        this.form.title = d.title || "";
        this.form.categoryId = d.categoryId || "";
        this.form.content = d.content || "";
        this.form.phone = d.phone || "";
      } catch (e) {
        alert("加载需求失败，请稍后重试");
      }
    },
    // 获取分类列表
    async getCategories() {
      try {
        const res = await this.$axios({
          url: "demandcat/list",
        });
        if (res.code === 200) {
          this.categories = res.data || [];
          this.form.categoryId = this.categories[0].id;
        }
      } catch (error) {
        console.error("获取分类失败:", error);
      }
    },

    // 获取需求列表
    async getRequests() {
      try {
        const res = await this.$axios({
          url: "demand/newest",
          method: "GET",
        });
        if (res.code === 200) {
          this.requests = res.data || [];
        }
      } catch (error) {
        console.error("获取需求列表失败:", error);
        // 如果有错误，显示空列表
        this.requests = [];
      }
    },

    // 打开需求详情
    openRequestDetail(request) {
      globalState.openRequestModal(request.id, this.categories);
    },

    // 提交表单
    async handleSubmit() {
      // 访客模式：提交前二次校验登录
      if (!requireLogin(this, "提出需求")) return;
      // 表单验证
      if (!this.form.title.trim()) {
        alert("请填写需求标题");
        return;
      }
      if (!this.form.categoryId) {
        alert("请选择需求分类");
        return;
      }
      if (!this.form.content.trim()) {
        alert("请填写详细描述");
        return;
      }

      this.submitting = true;

      try {
        const submitData = {
          title: this.form.title,
          categoryId: this.form.categoryId,
          content: this.form.content,
          phone: this.form.phone,
        };
        if (this.editingId) submitData.id = this.editingId;

        console.log(submitData);
        const data = await this.$aesEncrypt(submitData);
        const res = await this.$axios({
          url: "demand",
          method: "PUT",
          data: {
            data,
          },
        });

        if (res.code === 200) {
          if (this.editingId) {
            alert("修改已保存");
            this.$router.push("/user");
            return;
          }
          alert("需求提交成功！");
          // 重置表单
          this.form = {
            title: "",
            categoryId: "",
            content: "",
            phone: "",
          };
          // 刷新需求列表
          this.getRequests();
        } else {
          alert("提交失败：" + (res.msg || "服务器错误"));
        }
      } catch (error) {
        console.error("提交失败:", error);
        alert("提交失败，请检查网络或稍后重试");
      } finally {
        this.submitting = false;
      }
    },
  },
};
Date.prototype.format = function (fmt) {
  var o = {
    "M+": this.getMonth() + 1, //月份
    "d+": this.getDate(), //日
    "h+": this.getHours(), //小时
    "m+": this.getMinutes(), //分
    "s+": this.getSeconds(), //秒
    "q+": Math.floor((this.getMonth() + 3) / 3), //季度
    S: this.getMilliseconds(), //毫秒
  };
  if (/(y+)/.test(fmt))
    fmt = fmt.replace(
      RegExp.$1,
      (this.getFullYear() + "").substr(4 - RegExp.$1.length),
    );
  for (var k in o)
    if (new RegExp("(" + k + ")").test(fmt))
      fmt = fmt.replace(
        RegExp.$1,
        RegExp.$1.length == 1 ? o[k] : ("00" + o[k]).substr(("" + o[k]).length),
      );
  return fmt;
};
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
